import { format } from 'date-fns';
import { XMLParser } from 'fast-xml-parser';
import { snakeCase } from 'lodash'; // Using lodash for simple string conversion
import validator from 'validator';
import { z } from 'zod';
import type { DateRange } from '../../app/modules/components/filter/dashboard/DatePickerFilter';
import getAllObjectKeys from '../../app/modules/helpers/getAllObjectKeys';

export const toAbsoluteUrl = (pathname: string) => process.env.PUBLIC_URL + pathname;

export const valueHandler = (value: string | string[] | undefined) => {
  if (value === undefined || value === null) {
    return '—';
  }
  if (Array.isArray(value) && value.length === 0) {
    return '—';
  }
  if (value === '') {
    return '—';
  }

  return value;
};

export const xmlToJson = (xml) => {
  const xmlParser = new XMLParser();
  const options = {
    attributeNamePrefix: '',
    attrNodeName: '_attributes', //default is 'false'
    textNodeName: '_text',
    ignoreAttributes: false,
    ignoreNameSpace: false,
    allowBooleanAttributes: false,
    parseNodeValue: false,
    parseAttributeValue: false,
    parseTrueNumberOnly: true,
    trimValues: false,
    decodeHTMLchar: false,
    cdataTagName: '_cdata', //default is 'false'
    cdataPositionChar: '\\c',
  };

  return xmlParser.parse(xml, options);
};

export const randomAlphanum = (length: number) => {
  const alphanums = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  return Array.from({ length }, () => alphanums[Math.floor(Math.random() * alphanums.length)]).join(
    ''
  );
};

export const responseToJson = async (response: Response) => {
  if (!response.ok) throw new Error('Network response was not ok');

  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/xml')) {
    return xmlToJson(await response.text());
  } else {
    return await response.json();
  }
};

export const toTitleCase = (str: string, enable: boolean = true) => {
  if (enable) {
    return str.replace(
      /\w\S*/g,
      (text) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
    );
  } else {
    return str;
  }
};

export const formatDateRangeToISODate = (date: DateRange) => {
  return {
    from: format(date.from, 'yyyy-MM-dd'),
    to: format(date.to, 'yyyy-MM-dd'),
  };
};

export const creditCardType = (cc: string) => {
  let amex = new RegExp('^3[47][0-9]{13}$');
  let visa = new RegExp('^4[0-9]{12}(?:[0-9]{3})?$');
  let cup1 = new RegExp('^62[0-9]{14}[0-9]*$');
  let cup2 = new RegExp('^81[0-9]{14}[0-9]*$');

  let mastercard = new RegExp('^5[1-5][0-9]{14}$');
  let mastercard2 = new RegExp('^2[2-7][0-9]{14}$');

  let disco1 = new RegExp('^6011[0-9]{12}[0-9]*$');
  let disco2 = new RegExp('^62[24568][0-9]{13}[0-9]*$');
  let disco3 = new RegExp('^6[45][0-9]{14}[0-9]*$');

  let diners = new RegExp('^3[0689][0-9]{12}[0-9]*$');
  let jcb = new RegExp('^35[0-9]{14}[0-9]*$');


  if (visa.test(cc)) {
      return 'VISA';
  }
  if (amex.test(cc)) {
      return 'AMEX';
  }
  if (mastercard.test(cc) || mastercard2.test(cc)) {
      return 'MASTERCARD';
  }
  if (disco1.test(cc) || disco2.test(cc) || disco3.test(cc)) {
      return 'DISCOVER';
  }
  if (diners.test(cc)) {
      return 'DINERS';
  }
  if (jcb.test(cc)) {
      return 'JCB';
  }
  if (cup1.test(cc) || cup2.test(cc)) {
      return 'CHINA_UNION_PAY';
  }
  return undefined;
};

export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const toPercentage = (value: number, total: number, precision = 2) => {
  return ((value / total) * 100).toFixed(precision);
};

// Browser detection function
export const getBrowserInfo = (
  userAgent: string
): {
  name: string;
  version: string;
} => {
  // Prioritize Edge and Firefox first
  let match = userAgent.match(/(Edg|Edge)\/([\d.]+)/);
  if (match) return { name: 'Edge', version: match[2] };

  match = userAgent.match(/Firefox\/([\d.]+)/);
  if (match) return { name: 'Firefox', version: match[1] };

  // Check Opera before Chrome
  match = userAgent.match(/(OPR|Opera)\/([\d.]+)/);
  if (match) return { name: 'Opera', version: match[2] };

  match = userAgent.match(/Chrome\/([\d.]+)/);
  if (match) return { name: 'Chrome', version: match[1] };

  // Safari requires special handling
  // Safari gives two version numbers: one technical in the Safari/xyz token, and one user-friendly in a Version/xyz token.
  if (userAgent.includes('Safari')) {
    match = userAgent.match(/Version\/([\d.]+)/);
    return { name: 'Safari', version: match ? match[1] : 'unknown' };
  }

  // Internet Explorer checks
  match = userAgent.match(/Trident\/.*rv:([\d.]+)/);
  if (match) return { name: 'IE', version: match[1] };

  match = userAgent.match(/MSIE ([\d.]+)/);
  if (match) return { name: 'IE', version: match[1] };

  return { name: 'unknown', version: 'unknown' };
};

export const isUrlValid = (url: string, requireProtocol?: boolean) => {
  try {
    const trimmedUrl = url.trim();
    const hasProtocol = /^https?:\/\//i.test(trimmedUrl);

    if (requireProtocol && !hasProtocol) return false;

    const urlToValidate = hasProtocol ? trimmedUrl : `http://${trimmedUrl}`;

    return validator.isURL(urlToValidate, {
      require_protocol: true,
      protocols: ['http', 'https'],
    });
  } catch (error) {
    return false;
  }
};

export const normalizeUrl = (url: string, protocol = 'https'): string => {
  if (!url?.trim()) return url;

  const hasProtocol = /^https?:\/\//i.test(url);
  if (hasProtocol) return url;

  return `${protocol}://${url.replace(/^\/+/, '')}`;
};

export const getFQDNFromString = (url: string) => {
  if (!url) return null;
  try {
    // Add protocol if missing
    let processedUrl = url;
    if (!/^https?:\/\//i.test(url)) {
      processedUrl = 'http://' + url;
    }

    const urlObj = new URL(processedUrl);
    return urlObj.hostname;
  } catch (error) {
    console.error('Invalid URL:', error);
    return null;
  }
};

// Formats a number with K/M/B suffixes for large values. Example: 1799 → "1.8K"
export const formatCompactNumber = (num: number): string => {
  const absNum = Math.abs(num);
  const sign = num < 0 ? '-' : '';

  const candidates = [
    { scale: 1e9, suffix: 'B' },
    { scale: 1e6, suffix: 'M' },
    { scale: 1e3, suffix: 'K' },
  ];

  for (const { scale, suffix } of candidates) {
    const val = absNum / scale;
    // Only consider this suffix when it yields a value >= 0.1 (so we don't show 0.0B etc.)
    if (val >= 0.1) {
      const oneDecimal = sign + val.toFixed(1) + suffix;
      if (oneDecimal.length <= 4) return oneDecimal;

      const noDecimal = sign + Math.round(val).toString() + suffix;
      if (noDecimal.length <= 4) return noDecimal;
      // otherwise try smaller suffix
    }
  }

  // If we reach here and number >= 1000, try K as a fallback with relaxed checks
  if (absNum >= 1e3) {
    const kVal = absNum / 1e3;
    const oneDecimal = sign + kVal.toFixed(1) + 'K';
    if (oneDecimal.length <= 4) return oneDecimal;
    const noDecimal = sign + Math.round(kVal).toString() + 'K';
    if (noDecimal.length <= 4) return noDecimal;
    return sign + Math.round(kVal).toString() + 'K';
  }

  return num.toLocaleString();
};

export const getTextDisplayWidth = (text: string, container: HTMLElement = document.body) => {
  const span = document.createElement('span');
  span.style.opacity = '0'; // Make the span invisible
  span.style.position = 'absolute'; // Position it off-screen
  span.style.whiteSpace = 'nowrap'; // Prevent wrapping
  span.innerText = text; // Set the text to measure
  container.appendChild(span); // Append span for measurement
  const width = span.clientWidth; // Get the width
  container.removeChild(span); // Clean up
  return width;
};

// Truncate a string in the middle
export const truncateStringInMiddle = (
  str: string,
  containerWidth: number, // in pixels
  endMinLength?: number
) => {
  const ellipsis = '\u2026';
  const ellipsisWidth = getTextDisplayWidth(ellipsis);
  const maxLength = Math.floor((containerWidth - ellipsisWidth) / getTextDisplayWidth('m')); // Assuming 'm' as the widest character

  if (str.length <= maxLength) return str; // If it already fits, return as is

  const startLength = Math.ceil((maxLength - ellipsis.length) / 2);
  const endLength =
    endMinLength && endMinLength < startLength
      ? endMinLength
      : Math.floor((maxLength - ellipsis.length) / 2);

  const start = str.slice(0, startLength);
  const end = str.slice(str.length - endLength);

  return start + ellipsis + end; // Combine start, ellipsis, and end
};

// Recursively finds all values for the specified `targetKey` in nested objects/arrays
export const findValuesByKey = (data: any, targetKey: string): string[] => {
  const values = new Set<string>();

  function traverse(obj: any) {
    if (!obj || typeof obj !== 'object') return;

    if (Array.isArray(obj)) {
      obj.forEach(traverse);
      return;
    }

    if (targetKey in obj) {
      const value = obj[targetKey];
      if (value != null && typeof value !== 'object') {
        values.add(String(value));
      }
    }

    Object.values(obj).forEach(value => {
      if (value && typeof value === 'object') {
        traverse(value);
      }
    });
  }

  traverse(data);
  return Array.from(values);
}

export const camelToSnakeCaseRecursive = (obj) => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(camelToSnakeCaseRecursive);
  }

  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const newKey = snakeCase(key);
      newObj[newKey] = camelToSnakeCaseRecursive(obj[key]);
    }
  }
  return newObj;
};

const snakeToCamelCase = (str: string): string => {
  return str.toLowerCase().replace(/(_\w)/g, (group) => {
    return group[1].toUpperCase();
  });
};

export const snakeToCamelCaseRecursive = (obj): any => {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(snakeToCamelCaseRecursive);
  }

  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const camelKey = snakeToCamelCase(key);
      newObj[camelKey] = snakeToCamelCaseRecursive(obj[key]);
    }
  }
  return newObj;
};

export const parseDate = (dateString: string) => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return undefined;
  return date;
};

export const getAllSchemaKeys = (schema: z.ZodTypeAny, prefix = ''): string[] => {
  // Handle Optional, Nullable, etc
  if (schema instanceof z.ZodOptional || schema instanceof z.ZodNullable) {
    return getAllSchemaKeys(schema.unwrap(), prefix);
  }

  // Handle Effects (like .refine)
  if (schema instanceof z.ZodEffects) {
    return getAllSchemaKeys(schema._def.schema, prefix);
  }

  // Handle Intersection (merge two objects' keys)
  if (schema instanceof z.ZodIntersection) {
    return [
      ...getAllSchemaKeys(schema._def.left, prefix),
      ...getAllSchemaKeys(schema._def.right, prefix),
    ];
  }

  // Core logic: handle Object
  if (schema instanceof z.ZodObject) {
    const shape = schema.shape;
    return Object.keys(shape).flatMap((key) => {
      const fullPath = prefix ? `${prefix}.${key}` : key;
      const subSchema = shape[key];

      // Recursively get child nodes, while preserving the current level
      const nestedKeys = getAllSchemaKeys(subSchema, fullPath);
      return nestedKeys.length > 0 ? nestedKeys : [fullPath];
    });
  }

  // Basic type or array node, return current path
  return prefix ? [prefix] : [];
};
